import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

import { injectedComponent, useText, EMAIL_REGEXP } from '@base/core';
import { buildVkIdRedirectUri, writeVkOAuthPkceSession, markVkOAuthFlowAsAuth } from '@base/utils/utils';
import { useEmailTypoSuggestion } from '@base/hooks/useEmailTypoSuggestion';
import EmailTypoSuggestion from '@base/components/EmailTypoSuggestion';
import emailTypoStyles from '@base/components/EmailTypoSuggestion.module.css';
import { Config, OAuthList, Scheme, ConfigResponseMode, OAuthName, WidgetEvents, Languages } from '@vkid/sdk';

export default injectedComponent(
  {
    name: 'AuthDialogBase',
    services: ['api'],
    styles: ['dialog', 'targetDialog', 'button', 'self'],
  },
  function AuthDialogBase({
    onSNLogin,
    login,
    onLoginChange,
    emailTypoSuggest,
    password,
    onPasswordChange,
    updating,
    authError,
    validationId,
    validity,
    validate,
    title,
    passwordPattern,
    passwordHint,
    passwordDisabled,
    beforeActionButton,
    actionButtonText,
    onAction,
    afterActionButton,
    services: { api },
    styles: {
      dialog: dialogStyles,
      targetDialog: targetDialogStyles,
      button: buttonStyles,
      self: selfStyles,
    },
    diSettings,
  }) {
    const text = useText();

    const socialProvider = diSettings?.socialProvider ?? null;
    const enterOnlyLogin = diSettings?.enterOnlyLogin ?? false;

    const showGoogleBtn = socialProvider === 'gaf' || socialProvider === 'g-y-vkid';
    const showAppleBtn = socialProvider === 'gaf';
    const showFacebookBtn = socialProvider === 'gaf';
    const showYandexBtn = socialProvider === 'y-vkid' || socialProvider === 'g-y-vkid';
    const showVkId = socialProvider === 'y-vkid' || socialProvider === 'g-y-vkid';
    const showSocialSection = socialProvider !== null;

    const [isPasswordShown, setPasswordShown] = useState(false);

    const {
      suggestion: emailSuggestion,
      check: checkEmailTypo,
      scheduleCheck: scheduleEmailTypoCheck,
      accept: acceptEmailTypo,
    } = useEmailTypoSuggestion({ enabled: !!emailTypoSuggest });

    const loginRef = useRef();
    const passwordRef = useRef();

    const validateOwn = () =>
      loginRef.current.checkValidity() && passwordRef.current.value.length > 0 && passwordRef.current.checkValidity();

    const handleLoginChange = (e) => {
      validate(validationId, validateOwn());
      const value = e.target.value.trim();
      onLoginChange(value);
      scheduleEmailTypoCheck(value);
    };

    const handleLoginBlur = (e) => {
      checkEmailTypo(e.target.value.trim());
    };

    const handleEmailTypoApply = () => {
      const corrected = acceptEmailTypo();
      if (corrected) onLoginChange(corrected);
    };

    const handleLoginPaste = (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text/plain').trim();
      document.execCommand('insertText', false, pasted);
    };

    const handlePasswordChange = (e) => {
      validate(validationId, validateOwn());
      onPasswordChange(e.target.value);
    };

    const handleEmailKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (!password) {
          passwordRef.current.focus();
        } else if (enterOnlyLogin ? login : login && password) {
          onAction();
        }
      }
    };

    const handlePasswordKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (login && password) {
          onAction();
        }
      }
    };

    const handlePasswordShow = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setPasswordShown(!isPasswordShown);
    };

    const handleGoogleLogin = () => {
      typeof onSNLogin === 'function'
        ? onSNLogin('google', api.baseUrl)
        : document.location.assign(api.baseUrl + '/auth/google');
    };

    const handleAppleLogin = () => {
      typeof onSNLogin === 'function'
        ? onSNLogin('apple', api.baseUrl)
        : document.location.assign(api.baseUrl + '/auth/apple');
    };

    const handleFacebookLogin = () => {
      typeof onSNLogin === 'function'
        ? onSNLogin('facebook', api.baseUrl)
        : document.location.assign(api.baseUrl + '/auth/facebook');
    };

    const handleYandexLogin = () => {
      typeof onSNLogin === 'function'
        ? onSNLogin('yandex', api.baseUrl)
        : document.location.assign(api.baseUrl + '/auth/yandex');
    };

    const vkIdContainerRef = useRef(null);
    const oauthListInstanceRef = useRef(null);
    const configInitializedRef = useRef(false);
    const isLocalDevHost =
      typeof window !== 'undefined' && window.location && window.location.hostname === 'local.enjoydev.io';

    const generateRandomString = (length) => {
      const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
      const randomValues = new Uint8Array(length);
      window.crypto.getRandomValues(randomValues);
      let result = '';
      for (let i = 0; i < length; i++) {
        result += CHARSET[randomValues[i] % CHARSET.length];
      }
      return result;
    };

    const generateState = () => generateRandomString(32);
    const generateCodeVerifier = () => generateRandomString(128);
    const generateDeviceId = () => generateRandomString(32);

    const generateCodeChallenge = async (codeVerifier) => {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digestBuffer = await window.crypto.subtle.digest('SHA-256', data);
      return btoa(String.fromCharCode.apply(null, new Uint8Array(digestBuffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    };

    const initVKIDWidget = async () => {
      if (isLocalDevHost) return;
      if (!api.config.appId || !vkIdContainerRef.current) return;
      if (configInitializedRef.current) return;

      try {
        const redirectUri = buildVkIdRedirectUri(api.config);
        const state = generateState();
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const deviceId = generateDeviceId();

        markVkOAuthFlowAsAuth();
        writeVkOAuthPkceSession(state, codeVerifier, deviceId);

        Config.init({
          app: Number(api.config.appId),
          redirectUrl: redirectUri,
          state,
          codeVerifier,
          codeChallenge,
          deviceId,
          responseMode: ConfigResponseMode.Redirect,
        });

        configInitializedRef.current = true;

        if (!OAuthList) return;

        const oauthList = new OAuthList();
        oauthList.render({
          container: vkIdContainerRef.current,
          oauthList: [OAuthName.VK, OAuthName.MAIL, OAuthName.OK],
          scheme: Scheme.LIGHT,
          lang: Languages.RUS,
          styles: { height: 56, borderRadius: 16 },
        })
          .on(WidgetEvents.ERROR, () => {})
          .on(WidgetEvents.AUTH, () => {});

        oauthListInstanceRef.current = oauthList;
      } catch (e) {}
    };

    useEffect(() => {
      validate(validationId, validateOwn());
    }, [login]);

    useEffect(() => {
      if (!showVkId) return;

      initVKIDWidget().catch(() => {});

      return () => {
        if (oauthListInstanceRef.current) {
          try {
            oauthListInstanceRef.current.destroy();
          } catch (e) {}
          oauthListInstanceRef.current = null;
        }
        configInitializedRef.current = false;
      };
    }, [showVkId, api.config.appId, api.config.originUrl]);

    const loginInput = (
      <input
        ref={loginRef}
        type="email"
        autoComplete="username"
        className={clsx(dialogStyles.formInput, {
          [emailTypoStyles.inputWarning]: !!emailSuggestion,
        })}
        placeholder={text('dialog.login.placeholder__email')}
        pattern={EMAIL_REGEXP}
        value={login}
        onChange={handleLoginChange}
        onBlur={handleLoginBlur}
        onPaste={handleLoginPaste}
        onKeyPress={handleEmailKeyPress}
      />
    );

    return (
      <div className={clsx(targetDialogStyles.dialog, selfStyles.authDialog)}>
        <h3 className={dialogStyles.dialogTitle}>{title}</h3>
        {showSocialSection && (
          <>
            <div className={selfStyles.authButtonGroup}>
              {showGoogleBtn && (
                <button
                  type="button"
                  className={clsx(selfStyles.authButton, selfStyles.authButton__google)}
                  onClick={handleGoogleLogin}
                  aria-label="Войти через Google"
                ></button>
              )}
              {showAppleBtn && (
                <button
                  type="button"
                  className={clsx(selfStyles.authButton, selfStyles.authButton__apple)}
                  onClick={handleAppleLogin}
                  aria-label="Войти через Apple"
                ></button>
              )}
              {showFacebookBtn && (
                <button
                  type="button"
                  className={clsx(selfStyles.authButton, selfStyles.authButton__facebook)}
                  onClick={handleFacebookLogin}
                  aria-label="Войти через Facebook"
                ></button>
              )}
              {showYandexBtn && (
                <button
                  type="button"
                  className={clsx(selfStyles.authButton, selfStyles.authButton__yandex)}
                  onClick={handleYandexLogin}
                  aria-label="Войти с Яндекс ID"
                ></button>
              )}
              {showVkId && !isLocalDevHost && (
                <div
                  ref={vkIdContainerRef}
                  className={selfStyles.vkIdWidgetContainer}
                  id="vk-id-widget-container"
                ></div>
              )}
            </div>
            <p className={selfStyles.dividerBox}>
              <span className={selfStyles.dividerText}></span>
              <span className={selfStyles.divider}></span>
            </p>
          </>
        )}
        <form className={clsx(dialogStyles.form, selfStyles.authForm)}>
          {emailTypoSuggest ? (
            <span className={emailTypoStyles.loginWrapper}>
              {loginInput}
              <EmailTypoSuggestion suggestion={emailSuggestion} onApply={handleEmailTypoApply} />
            </span>
          ) : (
            loginInput
          )}
          <span className={selfStyles.passwordWrapper} data-validated="false">
            <input
              ref={passwordRef}
              type={isPasswordShown ? 'text' : 'password'}
              autoComplete="current-password"
              className={clsx(dialogStyles.formInput, {
                [selfStyles.disabledInput]: passwordDisabled,
              })}
              placeholder={text('dialog.login.placeholder__password')}
              pattern={passwordPattern || '.{6,}'}
              value={password}
              onChange={handlePasswordChange}
              onKeyPress={handlePasswordKeyPress}
              disabled={passwordDisabled}
            />
            <button
              className={clsx(selfStyles.passwordVisibilityBtn, {
                [selfStyles.showPassword]: !isPasswordShown,
              })}
              onClick={handlePasswordShow}
              disabled={passwordDisabled}
            ></button>
          </span>
          {passwordHint && (
            <span className={selfStyles.passwordHint}>{passwordHint}</span>
          )}
          {authError && (
            <span className={clsx(dialogStyles.errorMessage, selfStyles.errorMessage)}>
              {(Array.isArray(authError) ? authError : String(authError).split('\n')).map((line, index) => (
                <span key={index} className={selfStyles.errorMessageLine}>
                  {line}
                </span>
              ))}
            </span>
          )}
          {beforeActionButton}
          <button
            type="button"
            className={clsx(buttonStyles.primaryBtn, buttonStyles.primaryBtn__medium, {
              [buttonStyles.primaryBtn__disabled]: updating || (!validity && !passwordDisabled),
            })}
            onClick={onAction}
            disabled={updating || (!validity && !passwordDisabled)}
          >
            {actionButtonText}
          </button>
          {afterActionButton}
        </form>
      </div>
    );
  }
);
